import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AccountBalance as BudgetIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ShoppingCart as ItemIcon,
  Straighten as UnitIcon,
  Numbers as NumbersIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import ListFilterBar from '../../common/ListFilterBar';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { BUDGET_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS, TEXT_PRIMARY, TEXT_SECONDARY } from '../../common/SaaSTokens';
import { canEditBudget } from '../../../utils/financialPermissions';

const CURRENT_YEAR = new Date().getFullYear();
const FILTER_DEBOUNCE_MS = 600;
const STATS_FETCH_PER_PAGE = 1000;

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatBudgetMonth = (month) => {
  const m = Number(month);
  if (!m || m < 1 || m > 12) return '—';
  return MONTH_LABELS[m - 1];
};

const compareBudgetsByTime = (a, b) => {
  const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0);
  if (yearDiff !== 0) return yearDiff;
  const monthDiff = (Number(b.month) || 0) - (Number(a.month) || 0);
  if (monthDiff !== 0) return monthDiff;
  return new Date(b.created_at || 0) - new Date(a.created_at || 0);
};

const formatRm = (value) =>
  `RM ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sumBudgetAmount = (items) =>
  (items || []).reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

const BudgetList = () => {
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  const navigate = useNavigate();
  const theme = useTheme();

  const [budgets, setBudgets] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [budgetStats, setBudgetStats] = useState({
    all: { count: 0, amount: 0 },
    year: { count: 0, amount: 0 },
  });

  const isFirstFetch = useRef(true);
  const filtersReady = useRef(false);

  const cellTextSx = { fontWeight: 500, color: alpha(TEXT_PRIMARY, 0.9) };
  const cellMutedSx = { fontWeight: 500, color: alpha(TEXT_SECONDARY, 0.95) };

  const filterOptions = [
    { name: 'date_from', label: 'Start date', type: 'date' },
    { name: 'date_to', label: 'End date', type: 'date' },
    { name: 'min_price', label: 'Min amount (RM)', type: 'number', min: 0, step: 0.01, placeholder: 'Min amount' },
    { name: 'max_price', label: 'Max amount (RM)', type: 'number', min: 0, step: 0.01, placeholder: 'Max amount' },
  ];

  const fetchBudgetStats = async () => {
    try {
      const [allMetaRes, allItemsRes, yearMetaRes, yearItemsRes] = await Promise.all([
        axiosInstance.get('/budgets', { params: { per_page: 1, page: 1 } }),
        axiosInstance.get('/budgets', { params: { per_page: STATS_FETCH_PER_PAGE, page: 1 } }),
        axiosInstance.get('/budgets', { params: { per_page: 1, page: 1, year: CURRENT_YEAR } }),
        axiosInstance.get('/budgets', { params: { per_page: STATS_FETCH_PER_PAGE, page: 1, year: CURRENT_YEAR } }),
      ]);

      const allItems = allItemsRes.data?.data ?? [];
      const yearItems = yearItemsRes.data?.data ?? [];

      setBudgetStats({
        all: {
          count: allMetaRes.data?.total ?? allItems.length,
          amount: sumBudgetAmount(allItems),
        },
        year: {
          count: yearMetaRes.data?.total ?? yearItems.length,
          amount: sumBudgetAmount(yearItems),
        },
      });
    } catch (err) {
      console.error('Error fetching budget statistics:', err);
    }
  };

  const fetchBudgets = async () => {
    try {
      if (isFirstFetch.current) {
        setInitialLoading(true);
      } else {
        setTableLoading(true);
      }
      const params = { page: currentPage, per_page: rowsPerPage };
      if (appliedFilters.min_price) params.min_price = appliedFilters.min_price;
      if (appliedFilters.max_price) params.max_price = appliedFilters.max_price;
      if (appliedFilters.date_from) params.date_from = appliedFilters.date_from;
      if (appliedFilters.date_to) params.date_to = appliedFilters.date_to;
      if (appliedFilters.year) params.year = appliedFilters.year;
      const response = await axiosInstance.get('/budgets', { params });
      if (response.data.data) {
        setBudgets(response.data.data);
      } else {
        setBudgets(Array.isArray(response.data) ? response.data : []);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      if (err.response?.data?.errors) {
        setError(Object.entries(err.response.data.errors).map(([f, m]) => `${f}: ${m.join(', ')}`).join('\n'));
      } else {
        setError('Failed to fetch budgets');
      }
      setBudgets([]);
    } finally {
      isFirstFetch.current = false;
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchBudgetStats();
  }, [user]);

  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      setAppliedFilters({ ...filters });
      return undefined;
    }
    const timer = setTimeout(() => setAppliedFilters({ ...filters }), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    if (user) fetchBudgets();
  }, [user, currentPage, rowsPerPage, appliedFilters]);

  const handleDelete = (budgetItem) => {
    if (!canEditBudget(user, budgetItem)) return;
    setSelectedBudget(budgetItem);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/budgets/${selectedBudget.id}`);
      await fetchBudgets();
      await fetchBudgetStats();
      setDeleteDialogOpen(false);
      setSelectedBudget(null);
      setSuccess(`Budget item "${selectedBudget.item}" has been deleted successfully!`);
    } catch (err) {
      console.error('Error deleting budget:', err);
      setError('Failed to delete budget item. Please try again.');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
    setCurrentPage(1);
    if (!newFilters.year) setActiveStatFilter('all');
    else if (String(newFilters.year) === String(CURRENT_YEAR)) setActiveStatFilter('current_year');
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setActiveStatFilter('all');
    setPage(0);
    setCurrentPage(1);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'current_year'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    if (nextFilter === 'current_year') {
      nextFilters.year = String(CURRENT_YEAR);
    } else {
      delete nextFilters.year;
    }
    setFilters(nextFilters);
    setPage(0);
    setCurrentPage(1);
  };

  const filteredBudgets = useMemo(() => {
    const matched = budgets.filter((b) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        b.item?.toLowerCase().includes(q)
        || b.user?.name?.toLowerCase().includes(q)
        || b.unit?.toLowerCase().includes(q)
        || b.remark?.toLowerCase().includes(q)
      );
    });
    return [...matched].sort(compareBudgetsByTime);
  }, [budgets, searchTerm]);

  const statsData = [
    {
      icon: BudgetIcon,
      value: budgetStats.all.count,
      label: `All Items · ${formatRm(budgetStats.all.amount)}`,
      color: 'info',
    },
    {
      icon: CalendarIcon,
      value: budgetStats.year.count,
      label: `${CURRENT_YEAR} · ${formatRm(budgetStats.year.amount)}`,
      color: 'primary',
    },
  ];

  const columns = [
    {
      key: 'item',
      label: 'Item',
      icon: ItemIcon,
      minWidth: 180,
      render: (b) => (
        <Typography variant="body1" sx={{ fontWeight: 500, color: alpha(TEXT_PRIMARY, 0.95) }}>
          {b.item}
        </Typography>
      ),
    },
    {
      key: 'period',
      label: 'Period',
      icon: CalendarIcon,
      minWidth: 130,
      render: (b) => (
        <Typography variant="body2" sx={cellTextSx}>
          {formatBudgetMonth(b.month)} {b.year ?? ''}
        </Typography>
      ),
    },
    {
      key: 'amount',
      label: 'Amount (RM)',
      icon: NumbersIcon,
      minWidth: 140,
      render: (b) => (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: b.amount != null ? theme.palette.success.light : alpha(TEXT_SECONDARY, 0.85),
          }}
        >
          {b.amount != null ? formatRm(b.amount) : '—'}
        </Typography>
      ),
    },
    {
      key: 'percentage',
      label: 'Usage Percentage',
      icon: PercentIcon,
      minWidth: 120,
      render: (b) => (
        <Typography variant="body2" sx={cellTextSx}>
          {b.percentage != null ? `${Number(b.percentage).toLocaleString()}%` : '—'}
        </Typography>
      ),
    },
    {
      key: 'user.name',
      label: 'Created By',
      icon: PersonIcon,
      minWidth: 160,
      render: (b) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.875rem',
            }}
          >
            {b.user?.name?.charAt(0)?.toUpperCase()
              || b.created_by?.charAt(0)?.toUpperCase()
              || 'U'}
          </Avatar>
          <Typography variant="body2" sx={cellTextSx}>
            {b.user?.name || b.created_by || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (b) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Budget" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/budgets/${b.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.1) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEditBudget(user, b) && (
            <>
              <Tooltip title="Edit Budget" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/budgets/edit/${b.id}`, { state: { budget: b } });
                  }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.1) },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Budget" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(b);
                  }}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: '0.9rem',
            background: alpha('#450a0a', 0.92),
            border: '1px solid rgba(248,113,113,0.3)',
            color: '#fca5a5',
            boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          }}
        />
      </Container>
    );
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <Loading message="Loading budgets list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Budget"
        subtitle="Track and manage budget items and allocations"
        icon={BudgetIcon}
        actionButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/budgets/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.22)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.34)' },
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
            }}
          >
            Add Budget
          </Button>
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          mb: 3,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.9rem',
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          color: '#fca5a5',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
        }}
      />

      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          mb: 3,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
        }}
      />

      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={activeStatFilter === 'current_year' ? 1 : 0}
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by item, user, unit, or remark..."
      />

      <ListBulkToolbar
        config={BUDGET_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: BUDGET_BULK_CONFIG.endpoint,
          setItems: setBudgets,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'budget item(s)',
          refetch: async () => {
            await fetchBudgets();
            await fetchBudgetStats();
          },
          setError,
        })}
      />

      <TableList
        columns={columns}
        data={filteredBudgets}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => {
          setPage(newPage);
          setCurrentPage(newPage + 1);
        }}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
          setCurrentPage(1);
        }}
        {...bulkTableProps(BUDGET_BULK_CONFIG, user, selectedIds, setSelectedIds)}
        loading={tableLoading}
        emptyState={{
          icon: <BudgetIcon />,
          title: 'No budget items found',
          description:
            searchTerm || filters.min_price || filters.max_price || filters.date_from || filters.date_to || filters.year
              ? 'Try adjusting your filters to see more budget items.'
              : 'Get started by creating your first budget item.',
        }}
        onRowClick={(b) => navigate(`/budgets/${b.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedBudget(null);
        }}
        onConfirm={confirmDelete}
        itemType="budget item"
        itemName={selectedBudget?.item}
        description="This action cannot be undone and will permanently remove the budget item."
        confirmLabel="Delete Budget"
      />
    </Container>
  );
};

export default BudgetList;
